import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import type { ConsumptionSuggestion } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import {
  addSuggestionToList,
  hideSuggestion,
  listConsumptionSuggestions,
} from '../../db/shopping.js';
import { useMayPlan } from '../../hooks/useModules.js';
import { useTimeZone, useToday } from '../../hooks/useToday.js';

/**
 * „Bald wieder dran“ (27.10.2026): was der Haushalt regelmäßig kauft und
 * dessen Abstand bald um ist — gerechnet auf dem Gerät aus der Geschichte der
 * Liste (`consumptionSuggestions`). Steht nur da, wenn es etwas vorzuschlagen
 * gibt, und nur für die, die auf die Liste schreiben dürfen.
 */
export function ConsumptionSuggestions({
  householdId,
}: {
  householdId: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const mayPlan = useMayPlan();
  const timeZone = useTimeZone();
  const today = useToday();
  const [added, setAdded] = useState<string | null>(null);
  const suggestions = useLiveQuery(
    () =>
      today && timeZone
        ? listConsumptionSuggestions(householdId, today, timeZone, db)
        : Promise.resolve([] as ConsumptionSuggestion[]),
    [householdId, today, timeZone],
    [] as ConsumptionSuggestion[],
  );

  if (!mayPlan) return null;
  if (suggestions.length === 0) {
    return added ? (
      <p role="status" className="text-sm text-good">
        {t('shopping.consumption.added', { name: added })}
      </p>
    ) : null;
  }

  return (
    <section aria-labelledby="consumption-heading" className="kw-tile flex flex-col gap-2">
      <h3 id="consumption-heading" className="kw-label">
        {t('shopping.consumption.heading')}
      </h3>
      <p className="text-xs text-ink-muted">{t('shopping.consumption.intro')}</p>
      <ul className="flex flex-col gap-1">
        {suggestions.map((suggestion) => (
          <li key={suggestion.key} className="flex flex-wrap items-center gap-2">
            <span className="min-w-40 flex-1">
              <span className="block truncate">{suggestion.name}</span>
              <span className="block text-xs text-ink-muted">
                {t('shopping.consumption.every', { count: suggestion.pattern.intervalDays ?? 0 })}
                {' · '}
                {t('shopping.consumption.since', { count: suggestion.pattern.daysSince ?? 0 })}
                {suggestion.pattern.confidence === 'low'
                  ? ` · ${t('shopping.consumption.uncertain', { count: suggestion.pattern.purchases })}`
                  : ''}
              </span>
            </span>
            <span className="flex shrink-0 gap-2">
              <button
                type="button"
                className="kw-btn kw-btn-small px-3"
                aria-label={t('shopping.consumption.addNamed', { name: suggestion.name })}
                onClick={() =>
                  void addSuggestionToList(suggestion, db).then(() => setAdded(suggestion.name))
                }
              >
                {t('shopping.consumption.add')}
              </button>
              <button
                type="button"
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
                aria-label={t('shopping.consumption.hideNamed', { name: suggestion.name })}
                onClick={() => void hideSuggestion(suggestion, db)}
              >
                {t('shopping.consumption.hide')}
              </button>
            </span>
          </li>
        ))}
      </ul>
      {added ? (
        <p role="status" className="text-sm text-good">
          {t('shopping.consumption.added', { name: added })}
        </p>
      ) : null}
    </section>
  );
}
